import { useCallback, useState } from "react";

interface UseCopyToClipboardReturn {
  isCopied: boolean;
  copyToClipboard: (text: string) => Promise<boolean>;
  error: Error | null;
}

/**
 * Hook for copying text to clipboard with feedback
 *
 * @param resetDelay - Time in ms before resetting the copied state (default: 2000ms)
 * @returns Object with isCopied state, copyToClipboard function, and error
 *
 * @example
 * const { isCopied, copyToClipboard } = useCopyToClipboard();
 *
 * <button onClick={() => copyToClipboard("Hello World")}>
 *   {isCopied ? "Copied!" : "Copy"}
 * </button>
 */
export function useCopyToClipboard(resetDelay = 2000): UseCopyToClipboardReturn {
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      if (!navigator?.clipboard) {
        const err = new Error("Clipboard not supported");
        console.warn(err);
        setError(err);
        return false;
      }

      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        setError(null);

        // Reset the copied state after the specified delay
        setTimeout(() => {
          setIsCopied(false);
        }, resetDelay);

        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to copy");
        console.warn("Failed to copy text:", error);
        setError(error);
        setIsCopied(false);
        return false;
      }
    },
    [resetDelay],
  );

  return { isCopied, copyToClipboard, error };
}
